/* eslint-disable react-hooks/rules-of-hooks */
"use client"

import type React from "react"

import {
  Button,
  Input,
  HStack,
  VStack,
  Box,
  Text,
  Spinner,
  SimpleGrid,
  IconButton,
  Tooltip,
  Badge,
} from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { BoxTitle } from "@/components/BoxTitle"
import { ModalAlert } from "@/components/ModalStyled/Modal"
import { useNavigate, useParams } from "react-router-dom"
import { useEffect, useState } from "react"
import { useFetchItem, useSave } from "../service"
import ModalRetorno from "@/components/components/ModalRetorno"
import { FieldTipoDeposito } from "@/components/Fields/TipoDeposito"
import ActiveSwitch from "@/components/components/ActiveSwitch"

import { FaUpload, FaArrowLeft } from "react-icons/fa"
import { DepositoImport, type ImportDepositoData } from "../Components/import/ContratacaoImport"
import { OptionGroup } from "@/components/FormFields/OptionGroup"
import SimpleDatePicker from '@/components/DatePicker/index';


interface DepositoFormData {
  depositoerp: number | null
  nome: string
  tipo: number
  ativo: boolean
  id?: number
  situacao: string[]
  tipoCarga: string[]
  prioridade: string[]
  dataInicial?: string | null 
  dataFinal?: string | null 
  contratacaoInicial?: string | null 
  contratacaoFinal?: string | null 
}

type ModalMode = "form" | "import"

const ModalContratacao: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)

  const [modalMode, setModalMode] = useState<ModalMode>("form")
  const [isRetornoOpen, setIsRetornoOpen] = useState(false)
  const [mensagemRetorno, setMensagemRetorno] = useState("")
  const [tipoRetorno, setTipoRetorno] = useState<"sucesso" | "erro">("sucesso")
  const [importData, setImportData] = useState<ImportDepositoData[]>([])

  const { handleSubmit, setValue, watch, reset, control } = useForm<DepositoFormData>({
    defaultValues: {
      depositoerp: null,
      nome: "",
      tipo: 0,
      ativo: true,
      situacao: [],
      tipoCarga: [],
      prioridade: [],
      dataInicial: null, 
      dataFinal: null, 
      contratacaoInicial: null, 
      contratacaoFinal: null, 
    },
  })

  const { data, isLoading } = id ? useFetchItem(Number(id)) : { data: undefined, isLoading: false }

  const save = useSave(id)

  useEffect(() => {
    if (id && data) {
      reset(data)
    }
  }, [id, data, reset])

  const safeNumberConversion = (value: any): number | null => {
    if (value === undefined || value === null || value === "") {
      return null
    }

    const numValue = Number(value)
    if (isNaN(numValue)) {
      return null
    }

    return numValue
  }

  const onSubmit = async (formData: DepositoFormData) => {
    const depositoerpValue = safeNumberConversion(formData.depositoerp)
    const tipoValue = safeNumberConversion(formData.tipo)

    if (
      depositoerpValue === null ||
      depositoerpValue <= 0 ||
      !formData.nome.trim() ||
      tipoValue === null ||
      tipoValue <= 0
    ) {
      setMensagemRetorno("Preencha todos os campos obrigatórios com valores válidos.")
      setTipoRetorno("erro")
      setIsRetornoOpen(true)
      return
    }

    const parsedData = {
      ...formData,
      depositoerp: depositoerpValue,
      tipo: tipoValue,
    }

    try {
      await save.mutateAsync(parsedData)
      setMensagemRetorno(isEdit ? "Depósito atualizado com sucesso!" : "Depósito criado com sucesso!")
      setTipoRetorno("sucesso")
      setIsRetornoOpen(true)
    } catch (error) {
      setMensagemRetorno("Erro ao salvar depósito. " + ((error as any)?.message || ""))
      setTipoRetorno("erro")
      setIsRetornoOpen(true)
    }
  }

  const handleImportComplete = () => {
    setMensagemRetorno("Importação concluída com sucesso!")
    setTipoRetorno("sucesso")
    setIsRetornoOpen(true)
  }

  const setFileContentForProcess = (data: ImportDepositoData[]) => {
    setImportData(data)
  }

  const getModalTitle = () => {
    if (isEdit) {
      return "Editar Acomp. Contratação"
    }

    return modalMode === "form" ? "Novo Acomp. Contratação" : "Importar Acomp. Contratação"
  }

  const getModalSize = () => {
    return modalMode === "import" ? "4xl" : "3xl"
  }

  const renderHeader = () => (
    <HStack w={"100%"} justify="space-between" align="center">
      <HStack>
        {modalMode === "import" && !isEdit && (
          <Tooltip label="Voltar ao formulário">
            <IconButton
              aria-label="Voltar ao formulário"
              icon={<FaArrowLeft />}
              size="sm"
              variant="ghost"
              onClick={() => setModalMode("form")}
            />
          </Tooltip>
        )}
        <Text fontSize="xl" ml={2}>
          {getModalTitle()}
        </Text>
        {modalMode === "import" && importData.length > 0 && (
          <Badge colorScheme="blue" ml={2}>
            {importData.length} itens
          </Badge>
        )}
      </HStack>

      <HStack>
        {!isEdit && modalMode === "form" && (
          <Tooltip backgroundColor={"#117BAA"} borderRadius={"25px"} label="Importar em lote">
            <IconButton
              aria-label="Importar em lote"
              icon={<FaUpload />}
              size="sm"
              backgroundColor={"#117BAA"}
              variant="outline"
              onClick={() => setModalMode("import")}
            />
          </Tooltip>
        )}

        {modalMode === "form" && (
          <Box pr={12}>
            <ActiveSwitch size="sm" isChecked={watch("ativo")} onChange={() => setValue("ativo", !watch("ativo"))} />
          </Box>
        )}
      </HStack>
    </HStack>
  )

  const situacaoOptions = ["Em demanda", "Aguardando chegada"]
  const tipoCargaOptions = ["Frios", "Secos", "Misto"]
  const prioridadeOptions = ["Baixa", "Normal", "Urgente"]

  const renderBody = () => {
    if (modalMode === "import") {
      return (
        <Box m={2}>
          <DepositoImport
            onImportComplete={handleImportComplete}
            setFileContentForProcess={setFileContentForProcess}
          />
        </Box>
      )
    }

    return (
      <>
        {isEdit && isLoading ? (
          <Box textAlign="center">
            <Spinner />
          </Box>
        ) : (
          <Box m={2} as="form" onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={6} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Box>
                  <BoxTitle title="Código ERP do Depósito *">
                    <Input
                      type="number"
                      value={watch("depositoerp") ?? ""}
                      onChange={(e) => {
                        const val = e.target.value
                        const numValue = safeNumberConversion(val)
                        setValue("depositoerp", numValue)
                      }}
                      placeholder="Digite o código ERP"
                    />
                  </BoxTitle>
                </Box>
                <Box>
                  <BoxTitle title="Nome do Depósito *">
                    <Input
                      value={watch("nome")}
                      onChange={(e) => setValue("nome", e.target.value)}
                      placeholder="Digite o nome"
                    />
                  </BoxTitle>
                </Box>
              </SimpleGrid> 

            
              <HStack spacing={2}> 
                <Box>
                  <BoxTitle title="Data Inicial">
                    <SimpleDatePicker
                      onDateChange={(date: string) => setValue("dataInicial", date)}
                    />
                  </BoxTitle>
                </Box>
                <Box>
                  <BoxTitle title="Data Final">
                    <SimpleDatePicker
                      onDateChange={(date: string) => setValue("dataFinal", date)}
                    />
                  </BoxTitle>
                </Box>
                <Box>
                  <BoxTitle title="Contratação Inicial">
                    <SimpleDatePicker
                      onDateChange={(date: string) => setValue("contratacaoInicial", date)}
                    />
                  </BoxTitle>
                </Box>
                <Box>
                  <BoxTitle title="Contratação Final">
                    <SimpleDatePicker
                      onDateChange={(date: string) => setValue("contratacaoFinal", date)}
                    />
                  </BoxTitle>
                </Box>
              </HStack>

              <Box>
                <FieldTipoDeposito
                  isInvalid={false}
                  error={undefined}
                  //@ts-ignore
                  value={watch("tipo")}
                  onChange={(val: string) => {
                    const numValue = safeNumberConversion(val)
                    setValue("tipo", numValue || 0)
                  }}
                  required
                />
              </Box>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <OptionGroup
                  name="situacao"
                  label="Situação"
                  options={situacaoOptions}
                  control={control}
                  fillColor="#4CAF50"
                />
                <OptionGroup
                  name="tipoCarga"
                  label="Tipo de Carga"
                  options={tipoCargaOptions}
                  control={control}
                  fillColor="#FFC107"
                />
                <OptionGroup
                  name="prioridade"
                  label="Prioridade"
                  options={prioridadeOptions}
                  control={control}
                  fillColor="#E91E63"
                />
              </SimpleGrid>
            </VStack>
          </Box>
        )}
      </>
    )
  }

  const renderFooter = () => {
    if (modalMode === "import") {
      return <></>
    }

    return (
      <Button backgroundColor={"#117baa"} onClick={handleSubmit(onSubmit)} isLoading={save.isLoading} color={"white"}>
        {isEdit ? "Salvar Alterações" : "Confirmar"}
      </Button>
    )
  }

  return (
    <>
      <ModalAlert
        isOpen={true}
        xIsback
        size={getModalSize()}
        title={renderHeader()}
        body={renderBody()}
        footer={renderFooter()}
      />

      <ModalRetorno
        isOpen={isRetornoOpen}
        onClose={() => {
          setIsRetornoOpen(false)
          if (tipoRetorno === "sucesso") {
            if (modalMode === "import") {
              setModalMode("form")
              setImportData([])
            } else {
              navigate(-1)
            }
          }
        }}
        mensagem={mensagemRetorno}
        tipo={tipoRetorno}
      />
    </>
  )
}

export default ModalContratacao